import React, { useState } from 'react';
import { Switch } from 'antd';
import { C, FONT_UI } from '../../constants/design-tokens';
import { useEditorCanvas } from '../../canvas/hooks/use-editor-canvas';
import { useResolutions } from '../../resolution/hooks/use-resolutions';
import { useCanvasOverlay } from '../hooks/use-canvas-overlay';

/**
 * Self-contained overlay controls: manages its own toggle state and applies
 * the corresponding safe-zones / face-guides PNGs to the canvas.
 *
 * Overlay URLs are declared per-resolution in ResolutionConfig.overlays.
 * The panel only renders when the template declares at least one overlay
 * type in any resolution.
 */
export const OverlayControls: React.FC = () => {
  const { canvas, layoutVersion } = useEditorCanvas();
  const { resolutions, activeResolution } = useResolutions();

  const [showSafeZones, setShowSafeZones] = useState(false);
  const [showFaceGuides, setShowFaceGuides] = useState(false);

  const safeZonesUrl = activeResolution?.overlays?.safeZones;
  const faceGuidesUrl = activeResolution?.overlays?.faceGuides;

  useCanvasOverlay(
    canvas,
    safeZonesUrl,
    activeResolution?.canvasWidth ?? 0,
    activeResolution?.canvasHeight ?? 0,
    showSafeZones,
    layoutVersion
  );

  useCanvasOverlay(
    canvas,
    faceGuidesUrl,
    activeResolution?.canvasWidth ?? 0,
    activeResolution?.canvasHeight ?? 0,
    showFaceGuides,
    layoutVersion
  );

  const hasSafeZones = resolutions.some(r => r.overlays?.safeZones);
  const hasFaceGuides = resolutions.some(r => r.overlays?.faceGuides);

  if (!hasSafeZones && !hasFaceGuides) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 12,
        left: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        padding: '10px 12px',
        background: C.bg,
        borderRadius: 12,
        boxShadow: '0 2px 12px rgba(0, 0, 0, 0.10)',
        border: `1px solid ${C.panelBorder}`,
        zIndex: 10,
        pointerEvents: 'auto'
      }}
    >
      {hasSafeZones && (
        <OverlayRow label='Safe zones' checked={showSafeZones} onChange={setShowSafeZones} disabled={!safeZonesUrl} />
      )}
      {hasFaceGuides && (
        <OverlayRow label='Face grid' checked={showFaceGuides} onChange={setShowFaceGuides} disabled={!faceGuidesUrl} />
      )}
    </div>
  );
};

const OverlayRow: React.FC<{
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}> = ({ label, checked, onChange, disabled }) => (
  <label
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
      userSelect: 'none'
    }}
  >
    <Switch
      size='small'
      checked={checked}
      onChange={onChange}
      disabled={disabled}
      style={{ backgroundColor: checked ? C.accent : '' }}
    />
    <span style={{ fontFamily: FONT_UI, fontSize: 12, color: C.textPrimary, fontWeight: 500 }}>{label}</span>
  </label>
);
